import { call, CallEffect, put, PutEffect, select, SelectEffect } from 'redux-saga/effects';
import cloneDeep from 'lodash.clonedeep';

import { EditorUndoAction, editorUndoSuccess } from 'js/actionCreators/editorActions';
import { setActiveElements, setSelectedAudioClip, updateScribe } from 'js/actionCreators/scribeActions';
import { updateBitmapFonts } from 'js/actionCreators/fontActions';

import { getUndoObjects } from '../selectors';
import { clearCanvasSelectionReadyForUpdates } from '../sagaHelpers/clearCanvasSelectionReadyForUpdates';
import { areSameActiveElements } from '../sagaHelpers/areSameActiveElements';
import { isTextElementsChanged } from '../sagaHelpers/isTextElementsChanged';

export function* undo(
  action: EditorUndoAction
): Generator<SelectEffect | PutEffect | CallEffect, void, ReturnType<typeof getUndoObjects>> {
  const { scribeId } = action;
  const { activeScribe, activeScribePast, activeScribeFuture } = yield select(getUndoObjects, scribeId);

  // Do nothing if there is nothing in the undo history
  if (!activeScribePast.length || !activeScribe) return;

  yield call(clearCanvasSelectionReadyForUpdates);

  const newFuture = [cloneDeep(activeScribe), ...activeScribeFuture];
  const newActiveScribe = activeScribePast[activeScribePast.length - 1];

  const newPast = activeScribePast.slice(0, activeScribePast.length - 1);

  const newSelectedAudioClipId = newActiveScribe.selectedAudioClipId;
  const newSelectedAudioClip = newActiveScribe.audioClips?.find(audioClip => audioClip.id === newSelectedAudioClipId);
  yield put(editorUndoSuccess(newPast, newFuture));
  yield put(updateScribe(newActiveScribe, true));

  const areActiveElementsChanged = areSameActiveElements(
    (activeScribe.selectedElementIds, newActiveScribe.selectedElementIds)
  );
  if (areActiveElementsChanged) {
    yield put(setActiveElements(newActiveScribe.selectedElementIds));
  }
  yield put(setSelectedAudioClip(newSelectedAudioClip));

  const newTextElements = newActiveScribe.elements.filter(e => e.type === 'Text');
  const prevTextElements = activeScribe.elements.filter(e => e.type === 'Text');
  const isNeedUpdateBitmapFonts = isTextElementsChanged(newTextElements, prevTextElements);
  if (isNeedUpdateBitmapFonts) yield put(updateBitmapFonts(newTextElements));
}
